/* eslint-disable no-underscore-dangle */
import React from 'react';
import Head from 'next/head';

import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import getModuleTemplates from '../../utils/cms/getModuleTemplates';
import { Page, Content, PageWithNav } from '../../theme/page';
import { generateMeta, getMetaImage } from '../../utils/meta';
import InnerPageNavigation from '../../tissues/inner-page-navigation';

import isModuleBackgroundWhite from '../../utils/isModuleBackgroundWhite';
import isFullWidthBelowMedium from '../../utils/isFullWidthBelowMedium';
import isFullWidthMobileOnly from '../../utils/isFullWidthMobileOnly';
import hasHr from '../../utils/hasHr';

const isCircleBanner = (modulename) => modulename === 'circle_banner';
const isHeading = (modulename) => modulename === 'heading';
const isDoubleBanner = (modulename) => modulename === 'double_banner';
const isAccordion = (modulename) => modulename === 'accordion';
const isBlockQuote = (modulename) => modulename === 'block_quote';

const Preview = (props) => {
  // this page handles the rendering logic for all preview pages that are built
  // from CMS pages, not stories, or pillars. Those are handled by /algolia-cms/index
  const router = useRouter();
  const isStory = props.page.type === 'story';
  const isHomepage = props.page.url === '/index';
  const templates = getModuleTemplates(props.modules);

  return (
    <>
      <Head>
        {
          generateMeta({
            key: 'cms',
            title: props.page.meta_data?.title,
            description: props.page.meta_data?.description,
            og: {
              description: props.page.meta_data?.description,
              image: getMetaImage(props),
              title: props.page.meta_data?.title,
              url: `${process.env.NEXT_PUBLIC_CLIENT_HOST}${router.asPath}`
            },
            twitter: {
              description: props.page.meta_data?.description,
              image: getMetaImage(props),
              title: props.page.meta_data?.title
            }
          })
        }
      </Head>
      {props?.page?.innerPageNavigation ? (
        <Page background={props.page.background} id="maincontent">
          <PageWithNav>
            <InnerPageNavigation activePageName={props.page.url} />
            {templates.map((template, index) => {
              const {
                Template,
                data,
                title
              } = template;
              return Object.keys(data).length > 0 && (
                <Content
                  isCMS
                  hasHr={hasHr(data)}
                  noPadding={title === 'markup'}
                  key={`${title}-${data.id}`}
                  isCircleBanner={isCircleBanner(title)}
                  isWhite={isModuleBackgroundWhite(title)}
                  isHeading={isHeading(title)}
                  isDoubleBanner={isDoubleBanner(title)}
                  isAccordion={isAccordion(title)}
                  isFullWidthBelowMedium={isFullWidthBelowMedium(title)}
                  isFullWidthMobileOnly={isFullWidthMobileOnly(title)}
                  position={index}
                >
                  <Template
                    data={data}
                    isStory={isStory}
                    disableLazyload={index < 2}
                    homepage={isHomepage}
                  />
                </Content>
              );
            })}
          </PageWithNav>
        </Page>
      ) : (
        <Page background={props.page.background} id="maincontent">
          {templates.map((template, index) => {
            const {
              Template,
              data,
              title
            } = template;
            return Object.keys(data).length > 0 && (
            <Content
              isCMS
              hasHr={hasHr(data)}
              noPadding={title === 'markup'}
              key={`${title}-${data.id}`}
              isCircleBanner={isCircleBanner(title)}
              isWhite={isModuleBackgroundWhite(title)}
              isDoubleBanner={isDoubleBanner(title)}
              isAccordion={isAccordion(title)}
              isBlockQuote={isBlockQuote(title)}
              isNeon={props.page.background === 'neon'}
              isFullWidthBelowMedium={isFullWidthBelowMedium(title) && !isHomepage}
              isFullWidthMobileOnly={isFullWidthMobileOnly(title) && !isHomepage}
              position={index}
            >
              <Template
                data={data}
                isStory={isStory}
                disableLazyload={index < 2}
                homepage={isHomepage}
              />
            </Content>
            );
          })}
        </Page>
      )}
    </>
  );
};

Preview.defaultProps = {
  modules: [],
  page: {}
};

Preview.propTypes = {
  modules: PropTypes.array,
  page: PropTypes.object
};

export default Preview;
